import React, { useState, useEffect } from "react";
import { FaCheck, FaRegEdit, FaRegEye } from "react-icons/fa";
import { RiDeleteBin6Line } from "react-icons/ri";
import { MdKeyboardArrowLeft, MdKeyboardArrowRight } from "react-icons/md";
import { Link } from "react-router-dom";
import TableSkeleton from "../../TableSkeleton";

const CategoryTable = ({
  data,
  searchTerm,
  itemsPerPage = 10,
  handleEdit,
  resetFlag,
  openDeleteModal,
  selectedLanguage,
  isLoading = false,
}) => {
  const [selectedRows, setSelectedRows] = useState(
    new Array(data.length).fill(false)
  );
  const [isHeaderChecked, setIsHeaderChecked] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);

  // Skeleton columns configuration
  const skeletonColumns = [
    { width: 40, height: 16, isCircle: false, className: "pl-3" },
    { width: 60, height: 16, className: "pl-3" },
    { width: 80, height: 16, className: "pl-3" },
    { width: 200, height: 16, className: "pl-3" },
    { width: 300, height: 16, className: "pl-3" },
    { width: 120, height: 16, className: "text-center" },
  ];

  // Update the header checkbox state based on individual checkboxes
  useEffect(() => {
    setIsHeaderChecked(selectedRows.every((isSelected) => isSelected));
  }, [selectedRows]);

  useEffect(() => {
    setSelectedRows(new Array(data.length).fill(false));
    setIsHeaderChecked(false);
  }, [resetFlag, data.length]);

  const handleHeaderCheckboxChange = () => {
    const newSelectedRows = new Array(data.length).fill(!isHeaderChecked);
    setSelectedRows(newSelectedRows);
    setIsHeaderChecked(!isHeaderChecked);
  };

  const handleRowCheckboxChange = (index) => {
    const newSelectedRows = [...selectedRows];
    newSelectedRows[index] = !newSelectedRows[index];
    setSelectedRows(newSelectedRows);
    setIsHeaderChecked(newSelectedRows.every((isSelected) => isSelected));
  };

  // Filter categories based on search term and selected language
  const filterCategories = () => {
    return data.filter((category) => {
      const name =
        category.translations[selectedLanguage]?.name?.toLowerCase() || "";
      const metaTitle =
        category.translations[selectedLanguage]?.metaTitle?.toLowerCase() || "";
      return (
        name.includes(searchTerm.toLowerCase()) ||
        metaTitle.includes(searchTerm.toLowerCase())
      );
    });
  };

  // Calculate the total number of pages
  const totalPages = Math.ceil(filterCategories().length / itemsPerPage);

  // Handle page change
  const handlePageChange = (page) => {
    if (page > 0 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  // Slice the data based on the current page and itemsPerPage
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentData = filterCategories().slice(
    indexOfFirstItem,
    indexOfLastItem
  );

  return (
    <div>
      <div className="bg-white shadow-sm rounded-lg">
        <div className="flex items-center justify-between border-b border-dashed border-M-text-color/50 p-4">
          <div className="inline-flex items-center gap-3 flex-1">
            <h2 className="text-base font-medium text-gray-700 flex items-center gap-2">
              Category List
            </h2>
            {selectedRows.some((isSelected) => isSelected) && (
              <button
                onClick={() => {
                  const selectedCategoryIds = data
                    .map((category, index) =>
                      selectedRows[index] ? category.id : null
                    )
                    .filter((id) => id !== null);
                  openDeleteModal(selectedCategoryIds);
                }}
                className="size-7 bg-M-secondary-color/10 text-M-secondary-color hover:bg-M-secondary-color hover:text-white transition-all duration-300 text-base rounded-md inline-flex items-center justify-center"
                title="Delete selected rows"
              >
                <RiDeleteBin6Line />
              </button>
            )}
          </div>
          <Link
            to="/add-category"
            className="bg-[#73CB64] hover:bg-M-primary-color default-button shrink-0"
          >
            Add Category
          </Link>
        </div>

        <div className="overflow-x-auto">
          <table className="min-w-[600px] w-full divide-y divide-gray-200">
            <thead>
              <tr className="px-3 bg-[#FFFFFF]">
                 <th className="text-left">
                 <div className="relative left-3 -top-[3px]">
                   <input
                   type="checkbox"
                     id="category"                      className="hidden peer"                      checked={isHeaderChecked}
                     onChange={handleHeaderCheckboxChange}
                    />
                  <span className="h-4 w-4 border flex-none border-slate-400 rounded inline-flex items-center justify-center ltr:mr-3 rtl:ml-3 transition-all duration-150 bg-slate-100 peer-checked:bg-M-primary-color peer-checked:ring-1 peer-checked:ring-M-primary-color peer-checked:ring-offset-1 absolute top-[6px] left-0 z-0">
                      <FaCheck className="text-slate-100" />
                  </span>
                    <label
                      htmlFor="category"
                      className="cursor-pointer font-jost font-normal text-base text-slate-200 relative z-10 pl-6"
                    ></label>
                 </div>
                 </th>
                <th className="text-left uppercase font-inter font-medium text-sm text-M-heading-color p-3">
                  Serial No
                </th>
                <th className="text-left uppercase font-inter font-medium text-sm text-M-heading-color p-3">
                  Category ID
                </th>
                <th className="text-left uppercase font-inter font-medium text-sm text-M-heading-color p-3">
                  Name
                </th>
                <th className="text-left uppercase font-inter font-medium text-sm text-M-heading-color p-3">
                  Meta Title
                </th>
                <th className="text-center uppercase font-inter font-medium text-sm text-M-heading-color p-3">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                <TableSkeleton
                  rowCount={itemsPerPage}
                  columns={skeletonColumns}
                />
              ) : (
                currentData.map((category, index) => (
                  <tr
                    key={category.id}
                    className="border-b border-M-text-color/10 hover:bg-gray-100 border-collapse"
                  >
                    <td className="text-left capitalize font-inter font-medium text-sm text-M-heading-color">
                      <div className="relative left-3 -top-[3px]">
                        <input
                          type="checkbox"
                          id={"category" + index}
                          className="hidden peer"
                          checked={selectedRows[index]}
                          onChange={() => handleRowCheckboxChange(index)}
                        />
                        <span className="h-4 w-4 border flex-none border-gray-400 rounded inline-flex items-center justify-center ltr:mr-3 rtl:ml-3 transition-all duration-150 bg-gray-100 peer-checked:bg-M-primary-color peer-checked:ring-1 peer-checked:ring-M-primary-color peer-checked:ring-offset-1 absolute top-[6px] left-0 z-0">
                          <FaCheck className="text-gray-100" />
                        </span>
                        <label
                          htmlFor={"category" + index}
                          className="cursor-pointer font-jost font-normal text-base text-gray-200 relative z-10 pl-6"
                        ></label>
                      </div>
                    </td>
                    <td className="p-3 text-sm">{indexOfFirstItem + index + 1}</td>
                    <td className="p-3 text-sm">{category.id}</td>
                    <td className="p-3 text-sm">
                      {category.translations[selectedLanguage]?.name ||
                        "No Name"}
                    </td>
                    <td className="p-3 text-sm line-clamp-3">
                      {category.translations[selectedLanguage]?.metaTitle ||
                        "No MetaTitle"}
                    </td>
                    <td className="text-center p-3">
                      <div className="flex justify-center gap-2">
                        <button
                          onClick={() => handleEdit(category.id)}
                          className="size-7 rounded-full bg-M-Green-color/10 text-M-Green-color text-sm inline-flex items-center justify-center hover:bg-M-Green-color hover:text-white transition-all duration-300"
                          title="Edit"
                        >
                          <FaRegEdit />
                        </button>
                        <button
                          onClick={() => openDeleteModal([category.id])}
                          className="size-7 rounded-full bg-M-secondary-color/10 text-M-secondary-color text-sm inline-flex items-center justify-center hover:bg-M-secondary-color hover:text-white transition-all duration-300"
                          title="Delete"
                        >
                          <RiDeleteBin6Line />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination Area */}
        {!isLoading && totalPages > 1 && (
          <div className="p-5 text-end print-hidden">
            <ul className="inline-flex items-center justify-end border-collapse border border-M-text-color/20 rounded-md overflow-hidden">
                {/* Previous Button */}
                <li>
                  <button
                    className="table-pagination-item disabled:opacity-50"
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={currentPage === 1}
                  >
                    <MdKeyboardArrowLeft />
                  </button>
                </li>

                {/* Pagination Items */}
                {totalPages <= 10
                  ? // Show all pages if totalPages <= 10
                    [...Array(totalPages)].map((_, i) => (
                      <li key={i}>
                        <button
                          className={`table-pagination-item ${
                            currentPage === i + 1
                              ? "bg-M-primary-color text-white"
                              : ""
                          }`}
                          onClick={() => handlePageChange(i + 1)}
                        >
                          {i + 1}
                        </button>
                      </li>
                    ))
                  : // Show first 5, ellipsis, and last 3 if totalPages > 10
                    (() => {
                      const pages = [];
                      const firstPages = 5;
                      const lastPages = 3;

                      // Always show first 5 pages
                      for (
                        let i = 1;
                        i <= Math.min(firstPages, totalPages);
                        i++
                      ) {
                        pages.push(
                          <li key={i}>
                            <button
                              className={`table-pagination-item ${
                                currentPage === i
                                  ? "bg-M-primary-color text-white"
                                  : ""
                              }`}
                              onClick={() => handlePageChange(i)}
                            >
                              {i}
                            </button>
                          </li>
                        );
                      }

                      // Add ellipsis if there are pages between first 5 and last 3
                      if (totalPages > firstPages + lastPages) {
                        pages.push(
                          <li key="ellipsis" className="table-pagination-item">
                            ...
                          </li>
                        );
                      }

                      // Show last 3 pages
                      for (
                        let i = Math.max(
                          firstPages + 1,
                          totalPages - lastPages + 1
                        );
                        i <= totalPages;
                        i++
                      ) {
                        pages.push(
                          <li key={i}>
                            <button
                              className={`table-pagination-item ${
                                currentPage === i
                                  ? "bg-M-primary-color text-white"
                                  : ""
                              }`}
                              onClick={() => handlePageChange(i)}
                            >
                              {i}
                            </button>
                          </li>
                        );
                      }

                      return pages;
                    })()}

                {/* Next Button */}
                <li>
                  <button
                    className="table-pagination-item"
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={currentPage === totalPages}
                  >
                    <MdKeyboardArrowRight />
                  </button>
                </li>
              </ul>
          </div>
        )}
      </div>
    </div>
  );
};

export default CategoryTable;